import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ENTITY_TYPES, entityLabel } from '../config/entityRoutes';
import { fetchInvestigationGraph } from '../services/intelligenceApi';
import { clientToGraphCoords, layoutGraph, mergeLayoutWithOverrides, neighborSets, rootNodeId, truncateLabel } from '../utils/graphLayout';

const CANVAS_WIDTH = 920;
const CANVAS_HEIGHT = 560;
const DEPTH_OPTIONS = [1, 2, 3];
const DRAG_THRESHOLD = 4;

function InvestigationGraph({ open, onClose, entityType, entityId, title }) {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const graphGroupRef = useRef(null);
  const activeDragCleanupRef = useRef(null);

  const [graph, setGraph] = useState({ nodes: [], edges: [] });
  const [loading, setLoading] = useState(false);
  const [depth, setDepth] = useState(2);
  const [focus, setFocus] = useState({ entityType, entityId, title });
  const [history, setHistory] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const [positionOverrides, setPositionOverrides] = useState({});
  const [dragState, setDragState] = useState(null);
  const didDragRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setFocus({ entityType, entityId, title });
    setHistory([]);
    setSelectedNodeId(null);
    setHoveredNodeId(null);
    setTransform({ scale: 1, x: 0, y: 0 });
    setPositionOverrides({});
    setDragState(null);
    setDepth(2);
  }, [open, entityType, entityId, title]);

  useEffect(() => {
    if (!open || !focus.entityType || !focus.entityId) return undefined;

    let cancelled = false;
    setLoading(true);
    fetchInvestigationGraph(focus.entityType, focus.entityId, depth)
      .then((res) => {
        if (cancelled) return;
        setGraph(res.data || { nodes: [], edges: [] });
        setSelectedNodeId(rootNodeId(focus.entityType, focus.entityId));
      })
      .catch(() => {
        if (!cancelled) setGraph({ nodes: [], edges: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, focus.entityType, focus.entityId, depth]);

  useEffect(() => {
    setPositionOverrides({});
  }, [depth]);

  const rootId = rootNodeId(focus.entityType, focus.entityId);

  const layout = useMemo(
    () => layoutGraph(graph.nodes || [], graph.edges || [], rootId, {
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
    }),
    [graph.nodes, graph.edges, rootId]
  );

  const positionedNodes = useMemo(
    () => mergeLayoutWithOverrides(layout, positionOverrides),
    [layout, positionOverrides]
  );

  const positions = useMemo(
    () => Object.fromEntries(positionedNodes.map((node) => [node.id, node])),
    [positionedNodes]
  );

  const activeNodeId = hoveredNodeId || selectedNodeId;
  const highlight = useMemo(
    () => neighborSets(activeNodeId, graph.edges || []),
    [activeNodeId, graph.edges]
  );

  const selectedNode = selectedNodeId ? positions[selectedNodeId] : null;

  const expandNode = useCallback((node) => {
    if (!node?.entity_type || !node?.entity_id) return;
    if (node.entity_type === focus.entityType && node.entity_id === focus.entityId) return;

    setHistory((prev) => [...prev, focus]);
    setFocus({
      entityType: node.entity_type,
      entityId: node.entity_id,
      title: node.label || node.entity_id,
    });
    setTransform({ scale: 1, x: 0, y: 0 });
    setPositionOverrides({});
  }, [focus]);

  const goBack = useCallback(() => {
    setHistory((prev) => {
      if (!prev.length) return prev;
      const next = [...prev];
      const previous = next.pop();
      setFocus(previous);
      setTransform({ scale: 1, x: 0, y: 0 });
      setPositionOverrides({});
      return next;
    });
  }, []);

  const resetView = useCallback(() => {
    setTransform({ scale: 1, x: 0, y: 0 });
  }, []);

  const resetLayout = useCallback(() => {
    setPositionOverrides({});
  }, []);

  const zoomBy = useCallback((delta) => {
    setTransform((prev) => {
      const nextScale = Math.min(2.4, Math.max(0.55, prev.scale + delta));
      return { ...prev, scale: nextScale };
    });
  }, []);

  const onWheel = useCallback((event) => {
    event.preventDefault();
    zoomBy(event.deltaY > 0 ? -0.08 : 0.08);
  }, [zoomBy]);

  const clearActiveDrag = useCallback(() => {
    activeDragCleanupRef.current?.();
    activeDragCleanupRef.current = null;
    setDragState(null);
  }, []);

  useEffect(() => () => clearActiveDrag(), [clearActiveDrag]);

  const onCanvasPointerDown = useCallback((event) => {
    if (event.button !== 0) return;
    const target = event.target;
    if (typeof target.closest === 'function' && target.closest('.graph-node')) return;

    clearActiveDrag();
    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startY = event.clientY;
    const originX = transform.x;
    const originY = transform.y;

    setDragState({ kind: 'pan' });

    const onMove = (moveEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      setTransform((prev) => ({
        ...prev,
        x: originX + (moveEvent.clientX - startX),
        y: originY + (moveEvent.clientY - startY),
      }));
    };

    const onUp = (upEvent) => {
      if (upEvent.pointerId !== pointerId) return;
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      activeDragCleanupRef.current = null;
      setDragState(null);
    };

    activeDragCleanupRef.current = cleanup;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [clearActiveDrag, transform.x, transform.y]);

  const onNodePointerDown = useCallback((event, node) => {
    if (event.button !== 0) return;
    if (typeof event.target.closest === 'function' && event.target.closest('.graph-node-expand')) return;

    event.stopPropagation();
    event.preventDefault();
    clearActiveDrag();
    didDragRef.current = false;

    const svg = canvasRef.current?.querySelector('svg');
    const graphGroup = graphGroupRef.current;
    const pointerId = event.pointerId;
    const pointerStartX = event.clientX;
    const pointerStartY = event.clientY;
    const grabPoint = clientToGraphCoords(svg, event.clientX, event.clientY, graphGroup);
    const grabOffsetX = node.x - grabPoint.x;
    const grabOffsetY = node.y - grabPoint.y;
    const nodeId = node.id;

    setDragState({ kind: 'node', nodeId });

    const onMove = (moveEvent) => {
      if (moveEvent.pointerId !== pointerId) return;

      if (Math.hypot(moveEvent.clientX - pointerStartX, moveEvent.clientY - pointerStartY) >= DRAG_THRESHOLD) {
        didDragRef.current = true;
      }

      const current = clientToGraphCoords(svg, moveEvent.clientX, moveEvent.clientY, graphGroup);
      setPositionOverrides((prev) => ({
        ...prev,
        [nodeId]: {
          x: current.x + grabOffsetX,
          y: current.y + grabOffsetY,
        },
      }));
    };

    const onUp = (upEvent) => {
      if (upEvent.pointerId !== pointerId) return;
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      activeDragCleanupRef.current = null;
      setDragState(null);
    };

    activeDragCleanupRef.current = cleanup;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [clearActiveDrag]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'Backspace' && history.length) {
        event.preventDefault();
        goBack();
      }
      if (event.key === 'Enter' && selectedNode?.path) {
        navigate(selectedNode.path);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, goBack, history.length, navigate, selectedNode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !open) return undefined;
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [open, onWheel]);

  if (!open) return null;

  const nodeCount = graph.nodes?.length || 0;
  const edgeCount = graph.edges?.length || 0;
  const legendTypes = Object.keys(ENTITY_TYPES);

  return (
    <div className="graph-overlay-backdrop" onClick={onClose} role="presentation">
      <div className="graph-overlay" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="graph-overlay-header">
          <div>
            <h2>Investigation graph</h2>
            <p>{focus.title || `${entityLabel(focus.entityType)} · ${focus.entityId}`}</p>
          </div>
          <div className="graph-overlay-actions">
            {history.length > 0 && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={goBack}>
                ← Back
              </button>
            )}
            <button type="button" className="btn btn-secondary btn-sm" onClick={resetLayout}>
              Reset layout
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={resetView}>
              Reset view
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        <div className="graph-toolbar">
          <div className="graph-toolbar-group">
            <span className="graph-toolbar-label">Depth</span>
            {DEPTH_OPTIONS.map((value) => (
              <button
                key={value}
                type="button"
                className={`graph-chip ${depth === value ? 'active' : ''}`}
                onClick={() => setDepth(value)}
              >
                {value}
              </button>
            ))}
          </div>
          <div className="graph-toolbar-group">
            <button type="button" className="graph-chip" onClick={() => zoomBy(0.15)} aria-label="Zoom in">+</button>
            <button type="button" className="graph-chip" onClick={() => zoomBy(-0.15)} aria-label="Zoom out">−</button>
          </div>
          <div className="graph-toolbar-hint">
            Drag a node to pull it out of a cluster · Drag empty space to pan · Double-click to open
          </div>
        </div>

        <div
          ref={canvasRef}
          className={`graph-canvas-wrap ${dragState?.kind === 'pan' ? 'is-panning' : ''} ${dragState?.kind === 'node' ? 'is-node-dragging' : ''}`}
          onPointerDown={onCanvasPointerDown}
        >
          {loading ? (
            <div className="graph-loading">
              <div className="graph-loading-ring" />
              <span>Building graph…</span>
            </div>
          ) : nodeCount === 0 ? (
            <div className="graph-empty">
              <strong>No relationships yet</strong>
              <span>Promote searches to indicators or link entities to populate the graph.</span>
            </div>
          ) : (
            <svg viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`} className="investigation-graph-svg" role="img">
              <defs>
                <radialGradient id="graph-root-glow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="rgba(34, 211, 238, 0.35)" />
                  <stop offset="100%" stopColor="rgba(34, 211, 238, 0)" />
                </radialGradient>
              </defs>
              <rect width={CANVAS_WIDTH} height={CANVAS_HEIGHT} className="graph-canvas-bg" />
              <g
                ref={graphGroupRef}
                transform={`translate(${transform.x} ${transform.y}) scale(${transform.scale})`}
              >
                {(graph.edges || []).map((edge) => {
                  const source = positions[edge.source];
                  const target = positions[edge.target];
                  if (!source || !target) return null;

                  const isActive = highlight.edgeIds.has(edge.id);
                  const isDimmed = activeNodeId && !isActive;
                  const midX = (source.x + target.x) / 2;
                  const midY = (source.y + target.y) / 2;

                  return (
                    <g
                      key={edge.id}
                      className={`graph-edge-group ${isActive ? 'active' : ''} ${isDimmed ? 'dimmed' : ''}`}
                    >
                      <line
                        x1={source.x}
                        y1={source.y}
                        x2={target.x}
                        y2={target.y}
                        className="graph-edge"
                      />
                      {(isActive || edgeCount <= 8) && (
                        <text x={midX} y={midY} className="graph-edge-label">
                          {edge.label}
                        </text>
                      )}
                    </g>
                  );
                })}

                {positionedNodes.map((node) => {
                  const isRoot = node.id === rootId;
                  const isSelected = selectedNodeId === node.id;
                  const isHovered = hoveredNodeId === node.id;
                  const isNeighbor = highlight.nodeIds.has(node.id);
                  const isDimmed = activeNodeId && !isNeighbor;
                  const isDragging = dragState?.kind === 'node' && dragState.nodeId === node.id;
                  const canExpand = !isRoot && node.entity_type && node.entity_id;
                  const canOpen = Boolean(node.path);

                  return (
                    <g
                      key={node.id}
                      className={`graph-node ${isRoot ? 'root' : ''} ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''} ${isDimmed ? 'dimmed' : ''} ${isDragging ? 'is-dragging' : ''}`}
                      onPointerDown={(event) => onNodePointerDown(event, node)}
                      onMouseEnter={() => setHoveredNodeId(node.id)}
                      onMouseLeave={() => setHoveredNodeId(null)}
                      onClick={() => {
                        if (didDragRef.current) {
                          didDragRef.current = false;
                          return;
                        }
                        if (selectedNodeId === node.id && canExpand) {
                          expandNode(node);
                          return;
                        }
                        setSelectedNodeId(node.id);
                      }}
                      onDoubleClick={(event) => {
                        event.preventDefault();
                        if (canOpen) navigate(node.path);
                      }}
                    >
                      {isRoot && (
                        <circle cx={node.x} cy={node.y} r="46" className="graph-node-halo" fill="url(#graph-root-glow)" />
                      )}
                      <circle
                        cx={node.x}
                        cy={node.y}
                        r={isRoot ? 34 : 28}
                        className={`graph-node-circle graph-node-${node.entity_type}`}
                      />
                      <text x={node.x} y={node.y - (isRoot ? 42 : 36)} textAnchor="middle" className="graph-node-type">
                        {entityLabel(node.entity_type)}
                      </text>
                      <title>{node.label || node.entity_id}</title>
                      <text x={node.x} y={node.y + 4} textAnchor="middle" className="graph-node-label">
                        {truncateLabel(node.label || node.entity_id)}
                      </text>
                      {canExpand && isSelected && (
                        <g
                          className="graph-node-expand"
                          transform={`translate(${node.x + 22}, ${node.y - 22})`}
                          onClick={(event) => {
                            event.stopPropagation();
                            expandNode(node);
                          }}
                        >
                          <circle r="11" />
                          <text y="4" textAnchor="middle">↗</text>
                        </g>
                      )}
                    </g>
                  );
                })}
              </g>
            </svg>
          )}
        </div>

        <div className="graph-overlay-footer">
          <div className="graph-stats">
            <span>{nodeCount} nodes</span>
            <span>{edgeCount} edges</span>
            {selectedNode && (
              <span className="graph-selected-meta">
                Selected: {entityLabel(selectedNode.entity_type)} · {truncateLabel(selectedNode.label, 28)}
              </span>
            )}
          </div>
          <div className="graph-legend">
            {legendTypes.map((type) => (
              <span key={type} className="graph-legend-item">
                <span className={`graph-legend-dot graph-node-${type}`} />
                {entityLabel(type)}
              </span>
            ))}
          </div>
        </div>

        {selectedNode && (
          <div className="graph-selection-panel">
            <div>
              <div className="graph-selection-title">{truncateLabel(selectedNode.label, 32)}</div>
              <div className="graph-selection-subtitle">{entityLabel(selectedNode.entity_type)}</div>
            </div>
            <div className="graph-selection-actions">
              {selectedNode.id !== rootId && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => expandNode(selectedNode)}>
                  Expand graph
                </button>
              )}
              {selectedNode.path && (
                <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate(selectedNode.path)}>
                  Open entity
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default InvestigationGraph;
